import DOMPurify from 'dompurify';
import { SANITIZE_ALLOWED_STYLES as ALLOWED_STYLES, SANITIZE_CONFIG } from '../constants';

// Paste allows the same tags, attributes and link addresses as saved summaries
// (SANITIZE_CONFIG); everything else (scripts, iframes, fonts, sizes, classes)
// is stripped on paste/drop and the text inside removed tags is kept. Links
// keep their href only when it is an https:// address; other links become
// plain text. The PARAGRAPH_BLOCKS and TABLE_TAGS below then become paragraphs.

// Blocks the toolbar can't produce. They pass the DOMPurify step and are then
// rewritten as paragraphs, so their text keeps its own line.
const PARAGRAPH_BLOCKS = ['blockquote', 'pre', 'div'];
const TABLE_TAGS = ['table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td'];

// Every block tag that can be left after the DOMPurify step. This is not an
// allowlist: SANITIZE_CONFIG decides which tags are allowed, and this picks
// out the blocks among them, leaving out the inline tags (br, strong, b, em,
// i, u, s, strike, span, a). A paragraph can't contain blocks, so it is used
// in two places:
//   - inside a table cell, before the row becomes one paragraph, the blocks
//     it finds are removed and their content kept (tableRowToParagraph):
//       <tr><td><h1>Name</h1></td><td><p>Ada</p></td></tr>  ->  <p>Name | Ada</p>
//   - a quote, code block or div that contains a block is removed instead
//     of becoming a paragraph, and the blocks inside it are kept as they are
//     (blockToParagraph):
//       <div><h1>Title</h1><p>Text</p></div>  ->  <h1>Title</h1><p>Text</p>
// Pasted blocks anywhere else, such as a heading on its own, are not changed.
const BLOCK_SELECTOR = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li']
  .concat(PARAGRAPH_BLOCKS, TABLE_TAGS)
  .join(', ');

const TABLE_CELL_SEPARATOR = ' | ';

// Google Docs marks inline formatting with span styles instead of tags, e.g.
// bold copies as <span style="font-weight:700">Bold</span>. Each entry is
// [style name, test for its value, tag it becomes].
const STYLE_TO_TAG = [
  ['font-weight', (value) => value === 'bold' || Number(value) >= 600, 'strong'],
  ['font-style', (value) => value === 'italic', 'em'],
  ['text-decoration', (value) => value.includes('underline'), 'u'],
  ['text-decoration', (value) => value.includes('line-through'), 's'],
];

/**
 * Replaces an element with its own children, removing the tag but keeping its
 * content in place.
 *   <span>Hi <b>there</b></span>  ->  Hi <b>there</b>
 * @param {Element} element The element to remove
 */
const unwrap = (element) => {
  element.replaceWith(...element.childNodes);
};

/**
 * Replaces a table row with one paragraph holding its cells' content, separated
 * by TABLE_CELL_SEPARATOR. Blocks inside a cell are unwrapped so the paragraph
 * holds inline content only.
 *   <tr><td>Name</td><td><p>Ada</p></td></tr>  ->  <p>Name | Ada</p>
 * @param {HTMLTableRowElement} row The row to convert
 */
const tableRowToParagraph = (row) => {
  const paragraph = document.createElement('p');
  row.querySelectorAll(':scope > th, :scope > td').forEach((cell, index) => {
    cell.querySelectorAll(BLOCK_SELECTOR).forEach(unwrap);
    if (index > 0) paragraph.append(TABLE_CELL_SEPARATOR);
    paragraph.append(...cell.childNodes);
  });
  row.replaceWith(paragraph);
};

/**
 * Replaces a quote, code block or div with a paragraph, keeping its style for
 * the later filterStyles step. Line breaks inside <pre> become <br>. A block
 * that contains other blocks is unwrapped instead, since paragraphs can't
 * nest.
 *   <blockquote style="text-align:center">Quote</blockquote>
 *     ->  <p style="text-align:center">Quote</p>
 *   <pre>line 1\nline 2</pre>  ->  <p>line 1<br>line 2</p>
 *   <div><p>A</p><p>B</p></div>  ->  <p>A</p><p>B</p>
 * @param {Element} block The block to convert
 */
const blockToParagraph = (block) => {
  if (block.querySelector(BLOCK_SELECTOR)) {
    unwrap(block);
    return;
  }
  const paragraph = document.createElement('p');
  const style = block.getAttribute('style');
  if (style) paragraph.setAttribute('style', style);
  if (block.tagName === 'PRE') {
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach((node) => {
      const lines = node.data.replace(/\n$/, '').split('\n');
      node.replaceWith(
        ...lines.flatMap((line, index) => (index > 0 ? [document.createElement('br'), line] : [line]))
      );
    });
  }
  paragraph.append(...block.childNodes);
  block.replaceWith(paragraph);
};

/**
 * Rewrites an element's inline style to keep only ALLOWED_STYLES. A transparent
 * background-color is dropped because Google Docs sets it on every span, and
 * kept it would stop those spans from being unwrapped; it has no visible
 * effect. The style attribute is removed entirely when nothing is left.
 *   <span style="color:#ff0000;font-size:18pt;background-color:transparent">
 *     ->  <span style="color: rgb(255, 0, 0);">
 *   <span style="font-family:Arial">  ->  <span>
 * @param {Element} element The element whose style attribute is filtered
 */
const filterStyles = (element) => {
  const kept = ALLOWED_STYLES.map((name) => [name, element.style.getPropertyValue(name)]).filter(
    ([name, value]) => value && !(name === 'background-color' && value === 'transparent')
  );
  element.removeAttribute('style');
  kept.forEach(([name, value]) => element.style.setProperty(name, value));
  if (!element.getAttribute('style')) element.removeAttribute('style');
};

/**
 * Converts bold, italic, underline and line-through span styles into
 * <strong>, <em>, <u> and <s> tags nested inside the span, so the formatting
 * survives once filterStyles removes those styles. Matching tags are nested in
 * STYLE_TO_TAG order around the span's existing children.
 *   <span style="font-weight:700;font-style:italic">Hi</span>
 *     ->  <span style="font-weight:700;font-style:italic"><strong><em>Hi</em></strong></span>
 * filterStyles and the span unwrap then reduce this to <strong><em>Hi</em></strong>.
 * @param {HTMLSpanElement} span The styled span to convert
 */
const wrapStyledSpan = (span) => {
  const tags = STYLE_TO_TAG.filter(([name, matches]) =>
    matches(span.style.getPropertyValue(name))
  ).map(([, , tag]) => tag);
  let inner = span;
  tags.forEach((tag) => {
    const wrapper = document.createElement(tag);
    wrapper.append(...inner.childNodes);
    inner.append(wrapper);
    inner = wrapper;
  });
};

/**
 * Cleans pasted or dropped HTML down to what the summary editor supports.
 * Runs right before the rich-text editor inserts pasted content (the editor's
 * beforePasteInsert event), so the returned string is what ends up in the
 * summary.
 *
 * DOMPurify first removes disallowed tags (keeping their text) and unsafe
 * attributes and URLs. The remaining markup is then normalized: the Google
 * Docs <b style="font-weight:normal"> wrapper is removed, table rows, quotes,
 * code blocks and divs become paragraphs, formatting spans
 * become tags, styles are narrowed to ALLOWED_STYLES, and spans and links left
 * without attributes are unwrapped.
 *   <b style="font-weight:normal"><span style="font-weight:700;color:#ff0000">Hi</span>
 *   <script>alert(1)</script></b>
 *     ->  <span style="color: rgb(255, 0, 0);"><strong>Hi</strong></span>
 * @param {String|Node} html The pasted HTML, or a node, which is serialized first
 * @returns {String} The sanitized HTML
 */
function sanitizePastedHtml(html) {
  const source = typeof html === 'string' ? html : html?.outerHTML ?? html?.textContent ?? '';
  // The default DOMPurify instance, not sanitizeSummaryHtml: its style hook
  // would remove font-weight, font-style and text-decoration here, before
  // wrapStyledSpan turns them into tags. filterStyles narrows the styles later.
  const fragment = DOMPurify.sanitize(source, { ...SANITIZE_CONFIG, RETURN_DOM_FRAGMENT: true });

  // Google Docs wraps the whole clipboard in <b style="font-weight:normal">:
  //   <b style="font-weight:normal"><p>Text</p></b>  ->  <p>Text</p>
  fragment.querySelectorAll('b').forEach((b) => {
    if (b.style.fontWeight === 'normal') unwrap(b);
  });

  // Reversed so nested rows and blocks are converted before the ones around them.
  [...fragment.querySelectorAll('tr')].reverse().forEach(tableRowToParagraph);
  fragment.querySelectorAll(TABLE_TAGS.join(', ')).forEach(unwrap);
  [...fragment.querySelectorAll(PARAGRAPH_BLOCKS.join(', '))].reverse().forEach(blockToParagraph);

  fragment.querySelectorAll('span[style]').forEach(wrapStyledSpan);
  fragment.querySelectorAll('[style]').forEach(filterStyles);
  // Spans and links left with nothing to add are removed, keeping their text.
  // This only tidies the HTML; a bare span or link can't run code or change
  // the page. Google Docs wraps every run of text in a styled span, and the
  // style filter above can leave it empty; a link whose address isn't https://
  // loses its href in the DOMPurify step:
  //   <span style="font-family:Arial;font-size:11pt">Plain</span>  ->  Plain
  //   <a href="mailto:someone@odu.edu">email</a>  ->  email
  fragment.querySelectorAll('span:not([style]), a:not([href])').forEach(unwrap);

  const container = document.createElement('div');
  container.append(fragment);
  return container.innerHTML;
}

export default sanitizePastedHtml;
